import { type ReactNode } from "react";
import { formatPhone } from "shared/utils";
import { Show } from "shared/components";
import { AddressChip } from "./AddressChip";
import { distinctPhoneName } from "../utils/phoneLabel";

export interface MessageAddress {
  name: string;
  address: string;
}

interface MessageAddressFieldProps {
  label: string;
  address: MessageAddress;
  /** Pop-up (floating and drawer): 11px single "name • number" line. */
  compact?: boolean;
  // Rendered right after the number — used by the voice card for the redial
  // affordance the design puts on the To row.
  trailing?: ReactNode;
  /** Chips inside an outgoing bubble take the design's pink ground. */
  tinted?: boolean;
  isLine?: boolean;
}

/**
 * The "From: / To:" header row shared by the SMS bubble and the voice call card.
 * Renders the display name (when known) and the number, separated by a dot.
 */
export const MessageAddressField = ({
  label,
  address,
  compact,
  trailing,
  tinted,
  isLine
}: MessageAddressFieldProps) => {
  const name = distinctPhoneName(address.name, address.address);

  // Only the name truncates: the floating card leaves ~245px for the line and
  // a clipped number is useless.
  if (compact)
    return (
      <div className="flex items-center gap-1">
        <span className="w-8 shrink-0 font-medium text-content-1000">
          {label}
        </span>
        <Show if={Boolean(name)}>
          <span
            title={name}
            className="max-w-[300px] truncate font-medium text-content-1000"
          >
            {name}
          </span>
          <div className="size-0.5 shrink-0 rounded-full bg-content-100" />
        </Show>
        <span className="shrink-0 text-content-1000">
          {formatPhone(address.address)}
        </span>
        {trailing}
      </div>
    );

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-xs font-medium text-content-1000">
        {label}
      </span>
      <AddressChip
        address={formatPhone(address.address)}
        name={name}
        tinted={tinted}
        isLine={isLine}
      />
      {trailing}
    </div>
  );
};
